import React from "react";
import ClaimConversationAction from "./ClaimConversationAction";
import AssignmentControl from "./AssignmentControl";
import AgentResponseComposer from "./AgentResponseComposer";
import "./UnassignedConversationView.css";

/**
 * UnassignedConversationView
 * Main detail panel shown when the selected conversation has no
 * agent attached yet — surfaces the transcript plus Claim / Assign
 * actions front-and-center. Once assigned, the same panel can be
 * reused as the normal live conversation view.
 *
 * Props:
 *  - conversation: {
 *      id, visitorName, visitorMeta, messages: [{from, text}],
 *      status: "unassigned" | "assigned", assignedAgentId
 *    } | null
 *  - agents: [{ id, name, status }]
 *  - onClaim(id)
 *  - onAssign(id, agentId)
 *  - onSendMessage(id, text)
 */
export default function UnassignedConversationView({
  conversation,
  agents = [],
  onClaim = () => {},
  onAssign = () => {},
  onSendMessage = () => {},
  activeTask = "",
}) {
  if (!conversation) {
    return (
      <div className="cb-panel cb-conversation-panel">
        <div className="cb-conv-empty">
          Select a conversation from the queue to view details.
        </div>
      </div>
    );
  }

  const isUnassigned = conversation.status === "unassigned";
  const initials = conversation.visitorName
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="cb-panel cb-conversation-panel">
      <div className="cb-conv-header">
        <div className="cb-conv-visitor">
          <div className="cb-avatar">{initials}</div>
          <div>
            <div className="cb-conv-visitor-name">{conversation.visitorName}</div>
            <div className="cb-conv-visitor-meta">{conversation.visitorMeta}</div>
          </div>
        </div>

        <div className="cb-conv-header-actions">
          <div
            id="aw-task-claim"
            className={`aw-task-section aw-inline-task ${activeTask === "Claim conversation action" ? "is-active" : ""}`}
          >
            {isUnassigned && (
              <ClaimConversationAction
                conversationId={conversation.id}
                onClaim={(id) => onClaim(id)}
              />
            )}
          </div>
          <div
            id="aw-task-assignment"
            className={`aw-task-section aw-inline-task ${activeTask === "Assignment control" ? "is-active" : ""}`}
          >
            {!isUnassigned ? (
              <AssignmentControl
                agents={agents}
                assignedAgentId={conversation.assignedAgentId}
                onAssign={(agentId) => onAssign(conversation.id, agentId)}
              />
            ) : (
              <span className="cb-assignment-hint">Claim this conversation first to change its assignment.</span>
            )}
          </div>
        </div>
      </div>

      {isUnassigned && (
        <div className="cb-conv-unassigned-flag">
          <span>⚠ This conversation hasn't been claimed yet.</span>
        </div>
      )}

      <div className="cb-conv-body">
        {conversation.messages.map((m, i) => (
          <div className={`cb-bubble ${m.from}`} key={i}>
            {m.text}
          </div>
        ))}
      </div>

      <div
        id="aw-task-composer"
        className={`aw-task-section aw-inline-task ${activeTask === "Agent response composer" ? "is-active" : ""}`}
      >
        <AgentResponseComposer
          disabled={isUnassigned}
          onSend={(text) => onSendMessage(conversation.id, text)}
        />
      </div>
    </div>
  );
}
